import { DialogAwareButtonContainer, DialogAwareContentContainer, TokenInput, WalletGatedButton } from "@/components";
import { useDialogPortal, useWithdrawLockForm } from "@/hooks";
import { DIALOGS, TOKEN_IDS } from "@/types";
import { EXIT_LOCK_WHALE_RETENTION_THRESHOLD } from "../dialogs/ExitLock/exit-lock-whale-retain.config";
import type { ExitLockCapturedParams } from "../dialogs/ExitLock/exit-lock-capture-params.type";
import { useAccount } from "wagmi";
import { useUserTvl } from "@/hooks/infinifi/useUserTvl";
import { useCallback, useEffect, useRef } from "react";
import { PostHogEvent } from "@/posthog/posthog-capture-names";
import { PosthogHelper } from "@/posthog/posthog-helper";
import type { ExitFeedbackDialogProps } from "../dialogs/ExitLock/ExitLockFeedbackDialog";

interface WithdrawLockFormProps {
    existingLockAmount: number;
    existingLockDuration: number;
}

export function WithdrawLockForm({ existingLockAmount, existingLockDuration }: WithdrawLockFormProps) {
    const { address } = useAccount();
    const { userTotalTvl } = useUserTvl();

    const { amount, amountError, isLoading, setMaxAmount } = useWithdrawLockForm({
        existingLockAmount,
        existingLockDuration,
    });

    const { openDialog } = useDialogPortal();

    const handleSubmit = useCallback(
        (e?: React.FormEvent) => {
            // Prevent default form submission
            e?.preventDefault();

            // Validate amount doesn't exceed existingLockAmount
            const enteredAmount = Number(amount.value);

            const isAmountNumber = !isNaN(enteredAmount);
            const isEnteredAmountPositive = enteredAmount > 0;
            const isEnteredAmountWithinLimit = enteredAmount <= existingLockAmount;
            const isValidAmount = isAmountNumber && isEnteredAmountPositive && isEnteredAmountWithinLimit;
            if (!isValidAmount) {
                return;
            }

            const exitLockCaptureParams: ExitLockCapturedParams = {
                isWhaleToRetain: enteredAmount >= EXIT_LOCK_WHALE_RETENTION_THRESHOLD,
                exitSize: enteredAmount,
                lockDuration: existingLockDuration,
                positionSize: existingLockAmount,
                userAddress: address,
                userTotalTvl: userTotalTvl,
                whaleThreshold: EXIT_LOCK_WHALE_RETENTION_THRESHOLD,
            };

            openDialog({
                dialogType: DIALOGS.EXIT_FEEDBACK,
                dialogProps: {
                    exitLockCaptureParams,
                } satisfies ExitFeedbackDialogProps,
            });
        },
        [amount, existingLockAmount, existingLockDuration, address, userTotalTvl, openDialog],
    );

    const opened = useRef(false);

    useEffect(() => {
        if (!opened.current) {
            opened.current = true;
            // Dialog opened
            PosthogHelper.capture({
                event_name: PostHogEvent.WithdrawLockFormDialog.opened,
                properties: {
                    existingLockAmount,
                    existingLockDuration,
                },
                userIdentifier: address,
                allowTemporaryOptIn: false,
            });
        }
    }, [existingLockAmount, existingLockDuration, address]);

    return (
        <form onSubmit={(e) => handleSubmit(e)} className="w-[480px] max-w-full">
            <DialogAwareContentContainer isDialog className="space-y-4">
                <TokenInput
                    amount={amount}
                    amountError={amountError}
                    walletBalanceOverride={{
                        amount: existingLockAmount?.toString(),
                        balancePrefix: "Currently locked: ",
                    }}
                    selectedTokenId={TOKEN_IDS.IUSD}
                    handleTokenSelect={() => {}}
                    handleMaxClick={setMaxAmount}
                    tokenOptions={[TOKEN_IDS.IUSD]}
                    label="Enter amount to unlock"
                />
            </DialogAwareContentContainer>

            <DialogAwareButtonContainer isDialog>
                <WalletGatedButton
                    className="text-[14px] py-2.5 text-white"
                    type="submit"
                    isLoading={isLoading}
                    fullWidth
                    variant="primary"
                    disabled={
                        !amount.value ||
                        Number(amount.value) <= 0 ||
                        Number(amount.value) > existingLockAmount ||
                        !!amountError
                    }
                >
                    Confirm
                </WalletGatedButton>
            </DialogAwareButtonContainer>
        </form>
    );
}
